import { pascalCase } from 'change-case';
import { match, P } from 'ts-pattern';
import { JSON_SCHEMA_REFERENCE_PREFIX } from './helpers/schema';
import type {
  APIArraySchema,
  APIEnumSchema,
  APIEnumValue,
  APIListRequest,
  APIMethod,
  APIMethodAuthType,
  APIObjectProperty,
  APIObjectSchema,
  APIObjectValue,
  APIPackage,
  APIRefValue,
  APIRequestListOptions,
  APIRootSchema,
  APISchema,
  APIService,
  APISource,
  APIStateEntity,
} from './types/api';
import {
  BANG_TYPE_FIELD_NAME,
  type MethodTypeStateCommand,
  type PackageSummary,
  type ParsedAny,
  type ParsedArray,
  type ParsedAuthType,
  type ParsedBool,
  type ParsedBytes,
  type ParsedDate,
  type ParsedDecimal,
  type ParsedEntity,
  type ParsedEnum,
  type ParsedFloat,
  type ParsedInteger,
  type ParsedKey,
  type ParsedMap,
  type ParsedMethodListOptions,
  type ParsedObject,
  type ParsedObjectProperty,
  type ParsedObjectPropertyEntityKey,
  type ParsedObjectPropertyRules,
  type ParsedOneOf,
  type ParsedPackage,
  type ParsedPolymorph,
  type ParsedPolymorphProperties,
  type ParsedRef,
  type ParsedSchema,
  type ParsedSchemaWithRef,
  type ParsedService,
  type ParsedSource,
  type ParsedString,
  type ParsedTimestamp,
  type SortDirection,
} from './types/parsed';
import { EntityPart, type HTTPMethod, QueryPart, type SortingConstraintValue } from './types/shared';

function getSchemaRules(schema: APISchema): ParsedObjectPropertyRules | undefined {
  return match(schema)
    .returnType<ParsedObjectPropertyRules | undefined>()
    .with({ string: P.not(P.nullish) }, (s) => ({ string: s.string.rules }))
    .with({ integer: P.not(P.nullish) }, (s) => ({ integer: s.integer.rules }))
    .with({ float: P.not(P.nullish) }, (s) => ({ float: s.float.rules }))
    .with({ bool: P.not(P.nullish) }, (s) => ({ bool: s.bool.rules }))
    .with({ date: P.not(P.nullish) }, (s) => ({ date: s.date.rules }))
    .with({ timestamp: P.not(P.nullish) }, (s) => ({ timestamp: s.timestamp.rules }))
    .with({ decimal: P.not(P.nullish) }, (s) => ({ decimal: s.decimal.rules }))
    .with({ array: P.not(P.nullish) }, (s) => ({ array: s.array.rules, items: getSchemaRules(s.array.items) }))
    .with({ map: P.not(P.nullish) }, (s) => ({ map: s.map.rules }))
    .with({ object: P.not(P.nullish) }, (s) => ({ object: s.object.rules }))
    .with({ oneof: P.not(P.nullish) }, (s) => ({ oneOf: s.oneof.rules }))
    .with({ bytes: P.not(P.nullish) }, (s) => ({ bytes: s.bytes.rules }))
    .with({ key: P.not(P.nullish) }, () => undefined)
    .with({ enum: P.not(P.nullish) }, (s) => ({ enum: s.enum.rules }))
    .with({ any: P.not(P.nullish) }, () => undefined)
    .with({ polymorph: P.not(P.nullish) }, () => undefined)
    .exhaustive(() => undefined);
}

export function apiObjectPropertyToSource(
  parentEntity: APIStateEntity | undefined,
  property: APIObjectProperty,
  stateEntities: APIStateEntity[],
  schemas: Map<string, APISchema>,
): ParsedObjectProperty | undefined {
  const converted = apiSchemaToSource(property.schema, stateEntities, schemas);
  if (!converted) {
    return undefined;
  }

  const baseEntityKey = {
    shardKey: property.entityKey?.shardKey,
    tenant: property.entityKey?.tenant,
  } as const satisfies ParsedObjectPropertyEntityKey;

  return {
    ...property,
    schema: converted,
    rules: getSchemaRules(property.schema),
    entityKey: match(property)
      .with({ entityKey: { primary: true } }, () => {
        return {
          primary: parentEntity?.fullName || '',
          ...baseEntityKey,
        };
      })
      .with({ schema: { key: { ext: { foreign: P.not(P.nullish) } } } }, (s) => ({
        foreign: `${s.schema.key.ext.foreign.package}.${pascalCase(s.schema.key.ext.foreign.entity)}`,
        ...baseEntityKey,
      }))
      .otherwise(() => undefined),
  } as const satisfies ParsedObjectProperty;
}

function findStatusEnumValues(fullGrpcName: string, schemas: Map<string, APISchema>) {
  const schema = schemas.get(fullGrpcName);
  if (!schema || !('object' in schema) || !('properties' in schema.object)) {
    return [];
  }

  const properties = schema.object.properties.values();
  for (const property of properties) {
    if ('enum' in property.schema) {
      const enumSchema = match(property.schema)
        .with({ enum: { ref: P.not(P.nullish) } }, (s) => {
          const path = getFullGrpcPathForSchemaRef(s.enum.ref);
          return schemas.get(path) as APIEnumSchema<APIEnumValue> | undefined;
        })
        .with({ enum: P.not(P.nullish) }, (s) => s as APIEnumSchema<APIEnumValue> | undefined)
        .otherwise(() => undefined);

      if (enumSchema?.enum.entity?.part === EntityPart.Status) {
        return enumSchema.enum.options;
      }
    }
  }

  return [];
}

function normalizeApiEntityFullName(entity: APIStateEntity): string {
  const [prefix] = entity.fullName.split(`/${entity.name}`);

  return `${prefix}.${pascalCase(entity.name)}`;
}

function mapApiStateEntity(entity: APIStateEntity | undefined, schemas: Map<string, APISchema>, part?: EntityPart): ParsedEntity | undefined {
  if (!entity) {
    return undefined;
  }

  return {
    stateEntityFullName: normalizeApiEntityFullName(entity),
    schemaFullGrpcName: entity.schemaName,
    entity: entity.name,
    part: part || EntityPart.Unspecified,
    overview: entity.overview,
    primaryKeys: entity.primaryKey,
    events: entity.events || [],
    queryMethods: entity.queryService?.methods?.map((method) => method.fullGrpcName),
    commandMethods: entity.commandServices?.flatMap((service) => service.methods?.map((method) => method.fullGrpcName) || []),
    statuses: findStatusEnumValues(entity.schemaName, schemas),
    diagrams: entity.diagrams || [],
  };
}

function getFullGrpcPathForSchemaRef(ref: APIRefValue): string {
  return `${ref.package}.${ref.schema}`;
}

function getRefPathForApiSchemaRef(ref: APIRefValue): string {
  return `${JSON_SCHEMA_REFERENCE_PREFIX}${getFullGrpcPathForSchemaRef(ref)}`;
}

function buildApiSchemaRef(ref: APIRefValue): ParsedRef {
  return { $ref: getRefPathForApiSchemaRef(ref) };
}

export function apiPackageToSummary(pkg?: APIPackage): PackageSummary | undefined {
  if (!pkg) {
    return undefined;
  }

  return {
    package: pkg.name,
    label: pkg.label,
  };
}

export type APISchemaWithPackage = APISchema & {
  package: APIPackage;
};

export function apiSchemaToSource(
  schema: APISchema,
  stateEntities: APIStateEntity[],
  schemas: Map<string, APISchema>,
  fullGrpcName?: string,
  pkg?: APIPackage,
): ParsedSchemaWithRef | undefined {
  function mapObjectProperties(properties: APIObjectProperty[] | undefined) {
    return (properties || []).reduce<Map<string, ParsedObjectProperty>>((acc, curr) => {
      const matchingEntity = stateEntities.find((entity) => entity.schemaName === fullGrpcName);

      const mappedValue = apiObjectPropertyToSource(matchingEntity, curr, stateEntities, schemas);

      if (mappedValue) {
        acc.set(mappedValue.name, mappedValue);
      }

      return acc;
    }, new Map());
  }

  return match(schema)
    .with({ '!type': 'enum' }, (e) =>
      match(e.enum)
        .with({ ref: P.not(P.nullish) }, (enumWithRef): ParsedRef => buildApiSchemaRef(enumWithRef.ref))
        .with(
          P.not(P.nullish),
          (eWithEnum): ParsedEnum =>
            ({
              enum: {
                fullGrpcName: fullGrpcName!,
                name: eWithEnum.name,
                prefix: eWithEnum.prefix,
                rules: eWithEnum.rules || {},
                listRules: eWithEnum.listRules,
                docs: eWithEnum.docs,
                package: apiPackageToSummary(pkg),
                options: eWithEnum.options.map((option) => ({
                  name: option.name,
                  description: option.description,
                  number: option.number,
                  info: option.info,
                })),
              },
            }) as ParsedEnum,
        )
        .otherwise(() => undefined),
    )
    .with(
      { '!type': 'bool' },
      (b): ParsedBool => ({
        bool: {
          const: b.bool.const,
          rules: b.bool.rules || {},
          listRules: b.bool.listRules || {},
        },
      }),
    )
    .with(
      { '!type': 'integer' },
      (i): ParsedInteger => ({
        integer: {
          format: i.integer.format,
          rules: i.integer.rules || {},
          listRules: i.integer.listRules || {},
        },
      }),
    )
    .with(
      { '!type': 'float' },
      (f): ParsedFloat => ({
        float: {
          format: f.float.format,
          rules: f.float.rules || {},
          listRules: f.float.listRules || {},
        },
      }),
    )
    .with(
      { '!type': 'string' },
      (s): ParsedString => ({
        string: {
          format: s.string.format || '',
          rules: s.string.rules || {},
          listRules: s.string.listRules || {},
        },
      }),
    )
    .with(
      { '!type': 'date' },
      (s): ParsedDate => ({
        date: {
          rules: s.date.rules || {},
          listRules: s.date.listRules || {},
        },
      }),
    )
    .with(
      { '!type': 'timestamp' },
      (s): ParsedTimestamp => ({
        timestamp: {
          rules: s.timestamp.rules || {},
          listRules: s.timestamp.listRules || {},
        },
      }),
    )
    .with(
      { '!type': 'decimal' },
      (s): ParsedDecimal => ({
        decimal: {
          rules: s.decimal.rules || {},
          listRules: s.decimal.listRules || {},
        },
      }),
    )
    .with({ '!type': 'oneof' }, (o) =>
      match(o.oneof)
        .with({ ref: P.not(P.nullish) }, (oneOfWithRef): ParsedRef => buildApiSchemaRef(oneOfWithRef.ref))
        .with(
          P.not(P.nullish),
          (oneOf): ParsedOneOf => ({
            oneOf: {
              fullGrpcName: fullGrpcName!,
              description: oneOf.description,
              name: oneOf.name,
              properties: mapObjectProperties(oneOf.properties),
              rules: oneOf.rules || {},
              listRules: oneOf.listRules || {},
              package: apiPackageToSummary(pkg),
            },
          }),
        )
        .otherwise(() => undefined),
    )
    .with({ '!type': 'object' }, (o) =>
      match(o.object)
        .with({ ref: P.not(P.nullish) }, (objectWithRef): ParsedRef => buildApiSchemaRef(objectWithRef.ref))
        .with(P.not(P.nullish), (obj): ParsedObject => {
          let stateEntityFullGrpcName = fullGrpcName;

          if (obj.entity?.part === EntityPart.Event) {
            const stateEntity = stateEntities.find((entity) => entity.name === obj.entity?.entity);
            if (stateEntity) {
              stateEntityFullGrpcName = stateEntity.schemaName;
            }
          }

          const matchingStateEntity = stateEntities.find((entity) => entity.schemaName === stateEntityFullGrpcName);

          const entity: ParsedEntity | undefined =
            obj.entity || matchingStateEntity
              ? ({
                  stateEntityFullName:
                    matchingStateEntity?.fullName || (obj.entity?.entity && pkg?.name ? `${pkg.name}.${pascalCase(obj.entity.entity)}` : undefined),
                  name: matchingStateEntity?.name || obj.entity?.entity || '',
                  schemaFullGrpcName: stateEntityFullGrpcName,
                  part: obj.entity?.part,
                  ...mapApiStateEntity(matchingStateEntity, schemas, obj.entity?.part || EntityPart.Unspecified),
                } as ParsedEntity)
              : undefined;

          return {
            object: {
              fullGrpcName: fullGrpcName!,
              name: obj.name,
              description: obj.description,
              additionalProperties: obj.additionalProperties,
              properties: mapObjectProperties(obj.properties),
              rules: obj.rules || {},
              entity,
              polymorphMember: obj.polymorphMember,
              package: apiPackageToSummary(pkg),
            },
          };
        })
        .otherwise(() => undefined),
    )
    .with({ '!type': 'any' }, (a): ParsedAny => ({ any: { listRules: a.any.listRules || {} } }))
    .with({ '!type': 'polymorph' }, (p) => {
      return match(p.polymorph)
        .with({ ref: P.not(P.nullish) }, (polymorphWithRef): ParsedRef => buildApiSchemaRef(polymorphWithRef.ref))
        .with(P.not(P.nullish), (pm): ParsedPolymorph => {
          const properties: ParsedPolymorphProperties = new Map();

          if (pm.members) {
            for (const member of pm.members) {
              const memberProperties = new Map<string, ParsedObjectProperty>();

              memberProperties.set('!type', {
                name: BANG_TYPE_FIELD_NAME,
                required: true,
                schema: {
                  string: {
                    format: '',
                    rules: {},
                    literalValue: member,
                  },
                },
              });

              memberProperties.set('value', {
                name: 'value',
                schema: { $ref: `${JSON_SCHEMA_REFERENCE_PREFIX}${member}` },
                required: true,
              });

              properties.set(member, memberProperties);
            }
          }

          return {
            polymorph: {
              fullGrpcName: fullGrpcName!,
              name: pm.name,
              description: pm.description,
              package: apiPackageToSummary(pkg),
              members: pm.members,
              properties: properties.size ? properties : undefined,
            },
          };
        })
        .otherwise(() => undefined);
    })
    .with({ '!type': 'map' }, (m): ParsedMap | undefined => {
      const defaultStringSchema: ParsedString = {
        string: {
          format: '',
          rules: {},
        },
      };

      const convertedItemSchema = apiSchemaToSource(m.map.itemSchema, stateEntities, schemas, undefined, pkg);

      if (!convertedItemSchema) {
        return undefined;
      }

      return {
        map: {
          itemSchema: convertedItemSchema,
          keySchema: apiSchemaToSource(m.map.keySchema, stateEntities, schemas, undefined, pkg) || defaultStringSchema,
          rules: m.map.rules || {},
        },
      };
    })
    .with({ '!type': 'array' }, (a): ParsedArray | undefined => {
      const converted = apiSchemaToSource(a.array.items, stateEntities, schemas, undefined, pkg);

      if (!converted) {
        return undefined;
      }

      return {
        array: {
          itemSchema: converted,
          singleForm: a.array.ext?.singleForm,
          rules: a.array.rules || {},
        },
      };
    })
    .with({ '!type': 'bytes' }, (b): ParsedBytes => ({ bytes: { rules: b.bytes.rules } }))
    .with(
      { '!type': 'key' },
      (k): ParsedKey => ({
        key: {
          format: k.key.format,
          rules: k.key.rules,
          listRules: k.key.listRules || {},
          entity: k.key.ext?.foreign
            ? {
                package: k.key.ext.foreign.package,
                entity: pascalCase(k.key.ext.foreign.entity),
              }
            : undefined,
        },
      }),
    )
    .otherwise(() => {
      console.warn(`unsupported schema type while parsing api source: ${schema}`);
      return undefined;
    });
}

function getApiMethodRequestResponseFullGrpcName(method: APIMethod, requestOrResponse: APIRootSchema): string {
  const grpcNameBase = method.fullGrpcName.split('/').slice(0, -1).join('/');

  return match(requestOrResponse)
    .with({ object: { name: P.string } }, (o) => `${grpcNameBase}/${o.object.name}`)
    .with({ oneof: { name: P.string } }, (o) => `${grpcNameBase}/${o.oneof.name}`)
    .with({ enum: { name: P.string } }, (o) => `${grpcNameBase}/${o.enum.name}`)
    .otherwise(() => '');
}

function mapApiParameters(
  relatedEntity: APIStateEntity | undefined,
  parameters: APIObjectProperty[] | undefined,
  stateEntities: APIStateEntity[],
  schemas: Map<string, APISchema>,
  isPathParameter?: boolean,
) {
  if (!parameters?.length) {
    return undefined;
  }

  return parameters.reduce<Map<string, ParsedObjectProperty>>((acc, parameter) => {
    const converted = apiObjectPropertyToSource(relatedEntity, parameter, stateEntities, schemas);
    if (!converted) {
      return acc;
    }

    if (isPathParameter) {
      converted.required = true;
    }

    acc.set(converted.name, converted);

    return acc;
  }, new Map());
}

export function findMethodResponseRootSchema(
  response: APIObjectValue,
  packageName: string,
  parentRelatedEntity?: APIStateEntity,
): APIObjectSchema | undefined {
  if (parentRelatedEntity) {
    for (const property of response.properties || []) {
      const found = match(property)
        .with({ schema: { object: { ref: { package: packageName, schema: parentRelatedEntity.schemaName } } } }, (s) => s)
        .otherwise(() => undefined);

      if (found) {
        return found.schema;
      }
    }
  }

  // Check for an array first (there should be exactly one in a list method, as per j5 client handling)
  let foundArray: APIArraySchema<APIObjectSchema> | undefined;
  for (const property of response.properties || []) {
    const found = match(property)
      .with({ schema: { array: { items: { object: { ref: { package: P.not(P.nullish), schema: P.not(P.nullish) } } } } } }, (s) => s)
      .otherwise(() => undefined);

    if (found) {
      // if (foundArray) {
      //   console.warn(
      //     `multiple array schemas found in method response ${response.name}, root schema identification may not be accurate`,
      //   );
      // }

      foundArray = found.schema;
    }
  }

  if (foundArray) {
    return foundArray.array.items;
  }

  for (const property of response.properties || []) {
    const found = match(property)
      .with({ schema: { object: { ref: { package: P.not(P.nullish), schema: P.not(P.nullish) } } } }, (s) => s)
      .otherwise(() => undefined);

    if (found && found.schema.object.ref.package === packageName) {
      return found.schema;
    }
  }

  return undefined;
}

function mapApiAuth(auth: APIMethodAuthType | undefined): ParsedAuthType | undefined {
  return match(auth)
    .with({ '!type': 'cookie' }, () => ({ cookie: {} }))
    .with({ '!type': 'jwtBearer' }, (a) => ({ jwtBearer: { audience: Array.from(new Set(a.jwtBearer.audience)) } }))
    .with({ '!type': 'none' }, () => ({ none: {} }))
    .with({ '!type': 'custom' }, (s) => ({ custom: { passThroughHeaders: s.custom.passThroughHeaders } }))
    .otherwise(() => undefined);
}

function apiSortDirectionToParsedSortDirection(sortDirection: SortingConstraintValue | undefined): SortDirection | undefined {
  return match(sortDirection)
    .returnType<SortDirection | undefined>()
    .with('ASC', () => 'asc')
    .with('DESC', () => 'desc')
    .otherwise(() => undefined);
}

export function parseApiSource(source: APISource, registryVersion?: string): ParsedSource {
  const parsed: ParsedSource = {
    metadata: {
      builtAt: source.api.metadata.builtAt,
      version: source.version,
      registryVersion,
    },
    packages: [],
    schemas: new Map(),
  };

  const stateEntities = source.api.packages?.flatMap((pkg) => pkg.stateEntities || []);
  const schemas = new Map<string, APISchemaWithPackage>();

  // First, collect, then parse all root-level schemas
  for (const pkg of source.api.packages || []) {
    if (pkg.schemas) {
      for (const schemaName in pkg.schemas) {
        schemas.set(`${pkg.name}.${schemaName}`, { ...pkg.schemas[schemaName], package: pkg });
      }
    }
  }

  for (const [fullGrpcName, schema] of schemas) {
    const { package: pkg, ...rest } = schema;

    const parsedSchema = apiSchemaToSource(rest, stateEntities, schemas, fullGrpcName, pkg);

    if (parsedSchema) {
      parsed.schemas.set(fullGrpcName, parsedSchema as ParsedSchema);
    }
  }

  for (const pkg of source.api.packages || []) {
    const parsedPackage: ParsedPackage = {
      name: pkg.name,
      label: pkg.label,
      introduction: pkg.prose,
      hidden: pkg.hidden,
      services: [],
    };

    function mapListOptions(
      listOptions: APIRequestListOptions | undefined,
      listRequest: APIListRequest | undefined,
    ): ParsedMethodListOptions | undefined {
      if (!listOptions) {
        return undefined;
      }

      const options: ParsedMethodListOptions = {};

      if (listOptions.filterableFields) {
        options.filterableFields = listOptions.filterableFields.map((field) => {
          return {
            name: field.name,
            defaultValues: field.defaultFilters,
          };
        });

        options.defaultFilters = listOptions.filterableFields.reduce<Record<string, string[]>>((acc, curr) => {
          if (curr.defaultFilters) {
            acc[curr.name] = curr.defaultFilters;
          }

          return acc;
        }, {});
      }

      if (listOptions.searchableFields) {
        options.searchableFields = listOptions.searchableFields.map((field) => field.name);
      }

      if (listOptions.sortableFields) {
        options.sortableFields = listOptions.sortableFields.map((field) => ({
          name: field.name,
          defaultSort: apiSortDirectionToParsedSortDirection(field.defaultSort),
        }));

        const sortedSortables = [...listOptions.sortableFields].sort(
          (a, b) => (listRequest?.defaultSort?.indexOf(a.name) ?? 0) - (listRequest?.defaultSort?.indexOf(b.name) ?? 0),
        );

        options.defaultSorts = sortedSortables.reduce<Map<string, SortDirection>>((acc, curr) => {
          const sort = apiSortDirectionToParsedSortDirection(curr.defaultSort);
          if (sort) {
            acc.set(curr.name, sort);
          }

          return acc;
        }, new Map());
      }

      return options;
    }

    function mapService(
      service: APIService,
      schemas: Map<string, APISchema>,
      relatedEntity?: APIStateEntity,
      serviceType: 'query' | 'command' | 'service' = 'service',
    ) {
      const parsedService: ParsedService = {
        name: service.name,
        methods: [],
        parentPackage: parsedPackage,
        fullGrpcName: `${parsedPackage.name}.${service.name}`,
      };

      for (const method of service.methods || []) {
        const responseBodyAsObjectSchema = method.responseBody ? ({ '!type': 'object', object: method.responseBody } as APIObjectSchema) : undefined;
        const requestBodyAsObjectSchema = method.request?.body ? ({ '!type': 'object', object: method.request.body } as APIObjectSchema) : undefined;

        const responseBodyValue = match(responseBodyAsObjectSchema)
          .with({ object: { ref: P.not(P.nullish) } }, (o) => {
            const refValue = schemas.get(getFullGrpcPathForSchemaRef(o.object.ref));

            if (refValue && refValue['!type'] === 'object') {
              return refValue.object as APIObjectValue;
            }

            return undefined;
          })
          .with({ object: { name: P.string } }, (o) => o.object)
          .otherwise(() => undefined);

        let rootEntitySchema = responseBodyValue ? findMethodResponseRootSchema(responseBodyValue, parsedPackage.name, relatedEntity) : undefined;

        if (
          method.methodType?.stateQuery.queryPart &&
          (method.methodType.stateQuery.queryPart === QueryPart.List || method.methodType.stateQuery.queryPart === QueryPart.Get)
        ) {
          const fullStateEntityName = `${pkg.name}/${method.methodType.stateQuery.entityName}`;

          if (fullStateEntityName !== relatedEntity?.fullName) {
            relatedEntity = stateEntities.find((entity) => entity.fullName === fullStateEntityName);

            if (relatedEntity?.schemaName) {
              const potentialRoot = schemas.get(relatedEntity.schemaName);
              if (potentialRoot) {
                rootEntitySchema = potentialRoot as APIObjectSchema;
              }
            }
          }
        }

        const mappedRelatedEntity = relatedEntity ? mapApiStateEntity(relatedEntity, schemas, EntityPart.State) : undefined;
        const mappedPathParameters = mapApiParameters(relatedEntity, method.request?.pathParameters, stateEntities, schemas, true);
        const mappedQueryParameters = mapApiParameters(relatedEntity, method.request?.queryParameters, stateEntities, schemas);

        const methodType = match({ method, serviceType })
          .with({ method: { methodType: P.not(P.nullish) } }, (m) => m.method.methodType)
          .with({ serviceType: 'command' }, () => {
            if (!mappedRelatedEntity) {
              return undefined;
            }

            return {
              '!type': 'stateCommand',
              stateCommand: {
                entityName: mappedRelatedEntity.entity,
              },
            } as MethodTypeStateCommand;
          })
          .otherwise(() => undefined);

        parsedService.methods.push({
          name: method.name,
          description: method.description,
          fullGrpcName: method.fullGrpcName,
          httpMethod: method.httpMethod.toLowerCase() as HTTPMethod,
          httpPath: method.httpPath,
          rootEntitySchema: rootEntitySchema ? apiSchemaToSource(rootEntitySchema, stateEntities, schemas) : undefined,
          responseBody: responseBodyAsObjectSchema
            ? apiSchemaToSource(
                responseBodyAsObjectSchema,
                stateEntities,
                schemas,
                getApiMethodRequestResponseFullGrpcName(method, responseBodyAsObjectSchema),
                pkg,
              )
            : undefined,
          requestBody: requestBodyAsObjectSchema
            ? apiSchemaToSource(
                requestBodyAsObjectSchema,
                stateEntities,
                schemas,
                getApiMethodRequestResponseFullGrpcName(method, requestBodyAsObjectSchema),
                pkg,
              )
            : undefined,
          pathParameters: mappedPathParameters,
          queryParameters: mappedQueryParameters,
          listOptions: mapListOptions(method.request?.list, method.listRequest),
          relatedEntity: mappedRelatedEntity,
          parentService: parsedService,
          auth: mapApiAuth(method.auth),
          methodType,
        });
      }

      if (parsedService.methods.length) {
        parsedPackage.services.push(parsedService);
      }
    }

    pkg.stateEntities?.forEach((entity) => {
      if (entity.queryService) {
        mapService(entity.queryService, schemas, entity, 'query');
      }

      entity.commandServices?.forEach((service) => {
        mapService(service, schemas, entity, 'command');
      });
    });

    pkg.services?.forEach((service) => {
      mapService(service, schemas, undefined, 'service');
    });

    if (parsedPackage.services.length) {
      parsed.packages.push(parsedPackage);
    }
  }

  return parsed;
}
